import {  useRef } from "react";

/**
 * Custom hook that provides speech-to-text functionality using the Web Speech API.
 * 
 * @param {Function} callback - A callback function that receives the transcript of the recognized speech.
 * 
 * @returns {Object} An object containing two functions:
 * - `startListening`: Starts the speech recognition process and continuously listens for speech.
 * - `stopListening`: Stops the speech recognition process and prevents it from auto-restarting.
 * 
 * The hook uses `webkitSpeechRecognition` if available, falling back to `SpeechRecognition` otherwise.
 * It supports continuous listening and interim results, and is set to recognize English (en-US).
 * 
 * Note: This hook requires the browser to support the Web Speech API.
 */

export function useSpeechToText(callback) {
  const recognitionRef = useRef(null);

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window)) {
      // alert("Speech Recognition not supported");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true; // Keep listening
    recognition.interimResults = true; // Optional: Get live results
    recognition.lang = "en-US"; // Set your desired language

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0])
        .map((result) => result.transcript)
        .join(" ");
      callback(transcript);
    };

    recognition.onend = () => {
      // Restart if not manually stopped
      if (recognitionRef.current) {
        recognition.start();
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
    };

    recognition.start();
    recognitionRef.current = recognition;
  };

  const stopListening = () => {
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onend = null; // prevent auto-restart
      recognition.stop();
      recognitionRef.current = null;
    }
  };

  return { startListening, stopListening };
}
